import { Button } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorEnum } from '../../../enums/ApiErrorEnum';
import type FailedFile from '../../../interfaces/FailedFile';

/**
 * Maps API error enum values to translation keys for failed download reasons.
 */
const FAIL_REASON_KEYS: Record<string, string> = {
  [ApiErrorEnum.AntiCaptureActivated]: 'errors.antiCaptureActivated',
  [ApiErrorEnum.DownloadRestrictionActivated]: 'errors.downloadRestrictionActivated',
  [ApiErrorEnum.WatermarkProtectionDownloadDenied]: 'errors.watermarkProtectionDownloadDenied',
  [ApiErrorEnum.AntivirusFileInfected]: 'errors.antivirusFileInfected',
};

/**
 * Props for the FilesDownloadErrorForm component.
 */
interface Props {
  /** List of files that failed to download with error reasons. */
  failedFilesList?: FailedFile[];
  /** Handler to close/dismiss the error form. */
  onClose?: () => void;
}

/**
 * Displays a dialog listing files excluded from download due to restriction or error,
 * showing the file name and, if known, the specific error message.
 */
export const FilesDownloadErrorForm = ({ failedFilesList, onClose }: Props) => {
  const { t } = useTranslation();

  return (
    <div className="pt-2">
      {/* Informational description above the list */}
      <span className="text-gray-dark text-sm">{t('common.filesExcludedForRestrictions')}</span>
      {/* List excluded files with their reasons */}
      <div className="divide-gray-medium border-gray-medium mt-4 flex max-h-80 flex-col gap-2 divide-y overflow-auto border-y">
        {failedFilesList?.map((failedFile) => {
          // Find the translation key for the last error of this file
          const reasonKey = FAIL_REASON_KEYS[failedFile.lastError];
          return (
            <div key={failedFile.fileId} className="flex flex-col gap-1 px-1 py-1.5">
              {/* File name, with truncation and tooltip for visibility */}
              <span className="text-blue-dark truncate" title={failedFile.fileName}>
                {failedFile.fileName}
              </span>
              {/* Failure reason, using translation key or fallback */}
              <span className="text-red-dark text-xs">
                {reasonKey ? t(reasonKey) : t('common.failed')}
              </span>
            </div>
          );
        })}
      </div>
      {/* Close button, aligned right */}
      <div className="mt-4 flex flex-row items-center justify-end gap-2">
        <Button size="middle" type="primary" onClick={onClose}>
          {t('common.close')}
        </Button>
      </div>
    </div>
  );
};

export default FilesDownloadErrorForm;
